import type { ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Card } from '@/components/ui';
import { IconShield } from '@/components/ui/icons';
import { PageContainer } from '@/components/ui/PageContainer';
import { landingPath, permissionForPath } from './adminNav';
import { useAdminPermissionList } from '@/lib/useAdminPermission';
import { permissionMeta } from '@/lib/permissionCatalog';
import { adminLogout } from '@/lib/adminAuth';

/** Guards one routed page against the staffer's permission set.
 *
 *  Before this existed, ANY staffer could navigate to ANY page; the page's
 *  first query then 403'd, which the global handler mistook for a dead token
 *  and logged them out. Now the check happens BEFORE the page mounts, so an
 *  out-of-scope URL is a calm explanation instead of a session loss — and no
 *  request is wasted asking the server something we already know it'll refuse.
 *
 *  The client-side check is a UX affordance, not the security boundary:
 *  admin-svc enforces the same matrix and 403s regardless. */
export function RouteGuard({ to, children }: { to: string; children: ReactNode }) {
  const perms = useAdminPermissionList();
  const required = permissionForPath(to);

  if (!required || perms.includes(required)) return <>{children}</>;
  return <NoAccess required={required} perms={perms} />;
}

/** Sends a staffer to the first page they can actually open. Used for '/' and
 *  for unknown URLs, replacing the old hardcoded jump to Dashboard. */
export function LandingRedirect() {
  const perms = useAdminPermissionList();
  const to = landingPath(perms);
  if (!to) return <NoAccessAtAll />;
  return <Navigate to={to} replace />;
}

/** Shown when a staffer opens a page their role doesn't cover. Names the
 *  missing permission in plain language and offers the way back, rather than
 *  dumping them at a login form. */
function NoAccess({ required, perms }: { required: string; perms: readonly string[] }) {
  const meta = permissionMeta(required);
  const home = landingPath(perms);
  return (
    <PageContainer>
      <Card className="mx-auto mt-10 max-w-lg text-center">
        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-ink-850 text-text-muted">
          <IconShield className="h-5 w-5" />
        </div>
        <h2 className="text-[15px] font-semibold text-slate-100">You don't have access to this page</h2>
        <p className="mx-auto mt-2 max-w-sm text-[13px] text-text-secondary">
          It needs the “{meta.label}” permission, which your role doesn't include. Ask an administrator if you need it.
        </p>
        {home ? (
          <Link to={home} className="btn-primary mt-4 inline-flex">
            Back to {home.replace('/', '').replace(/-/g, ' ') || 'start'}
          </Link>
        ) : null}
      </Card>
    </PageContainer>
  );
}

/** The degenerate case: a role with no readable pages at all. Without this the
 *  landing redirect would have nowhere to go. */
function NoAccessAtAll() {
  return (
    <PageContainer>
      <Card className="mx-auto mt-10 max-w-lg text-center">
        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-ink-850 text-text-muted">
          <IconShield className="h-5 w-5" />
        </div>
        <h2 className="text-[15px] font-semibold text-slate-100">Your role has no pages yet</h2>
        <p className="mx-auto mt-2 max-w-sm text-[13px] text-text-secondary">
          You're signed in, but your role hasn't been granted access to anything in this portal. An administrator can
          add permissions on the Roles page.
        </p>
        <button type="button" onClick={adminLogout} className="btn-secondary mt-4">
          Sign out
        </button>
      </Card>
    </PageContainer>
  );
}
